<template>
  <fin-data-loader v-if="!$route.params.id && $route.name !== 'watchlistDetails'" :resolver="fetchData">

    <fin-editable-list
      v-model="watchlistLists"
      :create-btn-text="createBtnText"
      :create-item="createWatchlist"
      :description="description"
      :img="img"
      :img-max-width="275"
      :img-style="$vuetify.breakpoint.sm ? 'margin-top: -70px;' : $vuetify.breakpoint.mdAndUp ? 'margin-top: -80px;' : ''"
      :no-data="noData"
      :check-if-prompt-allowed="checkSubscription"
      :not-allowed-action="showSubscriptionDialog"
      :prompt="createPrompt"
      :update-item-name="updateWatchlistName"
      dialog-btn="Umbenennen"
      dialog-textfield-label="Watchlist Name"
      dialog-title="Watchlist umbennen"
      :list-title="watchlistSectionTitle"
      title-clickable
      @titleClicked="(val) => watchlistLists.find(wl => wl.id === val).inactive !== true ? $router.push({name: 'watchlistDetails', params: {id: val}}) : ''"
    >
      <template #item-subline="{item}">
        {{ item.watched.items.length }} {{ item.watched.items.length === 1 ? "Wertpapier" : "Wertpapiere" }}
      </template>

      <template #context-actions-subline="{item}">
        <div v-if="$vuetify.breakpoint.smAndUp && !item.inactive"
             :class="'mr-8 updated-at'"
             style="display: inline-block;">
          <fin-time-ago prependText="Letzte Änderung" :datetime="item.updatedAt" class="ml-1 d-inline"/>
        </div>
      </template>

      <template #icon v-if="!checkSubscription()">
        <fin-subscription-lock/>
      </template>

      <template v-if="$vuetify.breakpoint.smAndUp" #context-actions="{item}">
        <v-btn :disabled="item.inactive"
               :to="{name: 'watchlistDetails', params: {id: item.id}}"
               light
               outlined
               @click="$emit('edit', item)">
          Öffnen
        </v-btn>
        <v-icon v-if="hasInactiveWatchlists && !item.inactive"
                class="ml-3"
                @click="changeWatchlistStatusConfirm(item.id, item.name, true)">
          mdi-lock-open-variant
        </v-icon>
        <v-icon v-else-if="hasInactiveWatchlists && item.inactive"
                :color="$finColors.accentRed"
                class="ml-3"
                @click="changeWatchlistStatusConfirm(item.id, item.name, false)">
          mdi-lock
        </v-icon>
      </template>

      <template #context-menu="{item, showRename}">
        <v-list-item v-if="hasInactiveWatchlists" @click="showRename(item)">
          <v-list-item-title>
            {{ item.inactive ? 'Aktivieren' : 'Deaktivieren' }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item :disabled="item.inactive" @click="showRename(item)">
          <v-list-item-title>
            Umbenennen
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="startDeleteWatchlist(item)">
          <v-list-item-title>
            Löschen
          </v-list-item-title>
        </v-list-item>
      </template>
    </fin-editable-list>
  </fin-data-loader>

  <router-view v-else/>
</template>

<script>
import {finWatchlistAPI} from '@/js/finAPI'
import FinEditableList from '@/components/common/EditableList/index'
import FinDataLoader from '@/components/common/DataLoader/index'
import {mapGetters} from 'vuex'
import {EventBus} from "@/plugins/fincraft"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {itemInactiveStatus} from "@/js/finMutations"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import PriceModel from "@/mixins/PriceModel"
import FinSubscriptionLock from "@/components/common/subscription/lock"

export default {
  name: 'FinWatchlist',
  components: {FinSubscriptionLock, FinDataLoader, FinEditableList},
  data() {
    return {
      watchlistLists: [],
      img: require('@/assets/watchlist/watchlist.svg'),
      description_empty: 'Erstelle hier deine erste Watchlist und füge interessante Wertpapiere hinzu, um deren Entwicklung im Blick zu behalten.',
      description_notEmpty: 'Du kannst weitere Watchlists anlegen, wenn du deine Wertpapiere nach Themen oder Assetklassen strukturieren willst.',
      createBtnText: 'Neue Watchlist',
      createPrompt: {
        title: 'Wie soll deine Watchlist heißen?',
        dialogWidth: 448,
        confirmBtn: 'Speichern',
        input: true,
        inputText: '',
        inputLabel: 'Name deiner Watchlist'
      },
      noData: 'Du hast noch keine eigene Watchlist angelegt.'
    }
  },
  mixins: [PriceModel],
  computed: {
    ...mapGetters('auth', ['userId', 'profile']),
    description() {
      return this.watchlistLists.length ? this.description_notEmpty : this.description_empty
    },
    hasInactiveWatchlists() {
      return this.watchlistLists.find(w => w.inactive === true) !== undefined
    },
    maxWatchlists() {
      return this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.WATCHLIST.id)
    },
    watchlistSectionTitle() {
      let title = 'Deine Watchlists'

      if (this.watchlistLists.length > this.maxWatchlists) {
        title += '<span style="color: ' + this.$finColors.accentRed + '">'
      }

      title += '&nbsp;(' + this.watchlistLists.length + ' von ' + (this.maxWatchlists ? this.maxWatchlists : '-') + ')'

      if (this.watchlistLists.length > this.maxWatchlists) {
        title += '</span>'
      }

      return title
    },
  },
  methods: {
    async fetchData() {
      this.watchlistLists = await finWatchlistAPI.listWatchlists(this.userId)
    },
    checkSubscription() {
      return (this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.WATCHLIST.id) &&
        this.watchlistLists.length < this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.WATCHLIST.id))
    },
    showSubscriptionDialog() {
      let newCount = this.watchlistLists.length
      EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.WATCHLIST.id, PRICEMODEL_FEATURE_RESTRICTIONS.QUANTITY, {itemAmount: ++newCount})
    },
    async createWatchlist(name) {
      return await finWatchlistAPI.createWatchlist({'name': name})
    },
    async updateWatchlistName(item, name) {
      const wl = {
        id: item.id,
        name: name
      }

      try {
        const result = await finWatchlistAPI.renameWatchlist(wl)
        item = Object.assign(item, result)
      } catch (error) {
        console.error("error updating watchlist name", error)
        this.$toasted.global.finToast({
          message: 'Ein Fehler ist beim Aktualisieren deiner Watchlist %name% aufgetreten.',
          placeholders: [{name: "name", content: item.name}],
          type: 'error'
        })
      }
    },
    startDeleteWatchlist(item) {
      this.$confirm({
        title: 'Watchlist löschen',
        text: `Möchtest du die Watchlist ${item.name} wirklich löschen?`,
        dialogWidth: 448,
        confirmBtn: 'Löschen'
      })
        .then(() => {
          this.deleteWatchlist(item)
            .then(() => {
              const index = this.watchlistLists.findIndex(w => w.id === item.id)
              if (index > -1) {
                this.watchlistLists.splice(index, 1)
              }
              this.$toasted.global.finToast({
                message: 'Das Watchlist %name% wurde erfolgreich gelöscht.',
                placeholders: [{name: 'name', content: item.name}],
                type: 'success'
              })
            })
            .catch((e) => {
              console.error('deleteWatchlist failed', e)
              this.$toasted.global.finToast({
                message: 'Beim Löschen ist ein Fehler aufgetreten.',
                type: 'error'
              })
            })
        })
    },
    async deleteWatchlist(item) {
      await finWatchlistAPI.deleteWatchlist(item)
    },
    openNewlyCreatedWL(wl) {
      this.$router.push({name: 'watchlistDetails', params: {id: wl.id}})
    },
    changeWatchlistStatusConfirm(wlId, title, newStatus) {
      const text = []
      if (!newStatus) {
        const hint = 'Warum ist diese Watchlist gesperrt?'
        text.push({
          autoParse: false,
          type: "paragraph",
          text: hint,
          spans: [{
            "start": 0,
            "end": hint.length,
            "type": "crossref",
            "data": {"to": this.profile.stripeId ? "watchlistsperre-downgrade" : "watchlistsperre-paywall"}
          }]
        })
      }
      text.push({
        type: "paragraph",
        text: `Möchtest du die Watchlist "${title}" wirklich ` + (newStatus ? 'deaktivieren' : 'aktivieren') + '?',
        spans: [],
      })

      this.$confirm({
        title: 'Watchlist ' + (newStatus ? 'deaktivieren' : 'aktivieren'),
        text,
        dialogWidth: 400,
        confirmBtn: 'Ja',
        textWithLexikonLink: true,
      })
        .then(() => {
          this.changeWatchlistStatus({newStatus, wlId: wlId, title})
        }).catch(() => {
      })
    },
    async changeWatchlistStatus(statusObj) {
      console.log("MyWatchlists.changeWatchlistStatus", statusObj)

      try {
        await API.graphql(graphqlOperation(itemInactiveStatus, {
          featureId: PRICEMODEL_FEATURE_IDS.WATCHLIST.id,
          itemId: statusObj.wlId,
          newStatus: statusObj.newStatus,
        }))

        this.$emit('reloadWatchlists')
      } catch (error) {
        if (error.errors.length === 1 && error.errors[0].errorType === 'ActiveItemCountExceededException') {
          this.$confirm({
            title: 'Hinweis',
            text: 'Dein Abo erlaubt dir derzeit die Nutzung von ' + this.maxWatchlists + ' Watchlist(s).\n\nUm diese Watchlist aktiv zu nutzen, musst du andere Watchlists deaktivieren oder löschen.',
            dialogWidth: 400,
            textWithLexikonLink: true,
            cancelBtn: null,
          })
        } else {
          console.error('Error de/activating portfolio', error, statusObj)
          this.$toasted.global.finToast({
            message: `Beim ${statusObj.newStatus ? 'Deaktivieren' : 'Aktivieren'} deiner Watchlist %name% ist ein Fehler aufgetreten.`,
            placeholders: [{name: "name", content: statusObj.title}],
            type: 'error'
          })
        }
      }
    },
  },
  mounted() {
    EventBus.$on('edit', this.openNewlyCreatedWL)
  },
  beforeDestroy() {
    EventBus.$off('edit', this.openNewlyCreatedWL)
  }
}
</script>

<style scoped lang="scss">
@import "./../../styles/variables.scss";

.header {
  background-color: $fin-color-primary-dark;
  color: #ffffff;
}

::v-deep .content {
  min-height: 180px;

  .img {
    position: absolute;
    top: 0;
    right: 50px;
    width: 274px;
    height: 274px;
  }
}

.updated-at ::v-deep * {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: $fin-color-cool-grey;
}
</style>
